import * as React from "react";
import { cx } from "../internal/cx.js";
import type { BreadcrumbCrumb } from "../molecules/breadcrumb.js";
import { PageHeader } from "../organisms/page-header.js";

export type DashboardLayoutWidth = "default" | "wide" | "full";
export type DashboardLayoutStickyBarPosition = "sticky" | "static";

export interface DashboardLayoutProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  stickyBar?: React.ReactNode;
  stickyBarPosition?: DashboardLayoutStickyBarPosition;
  breadcrumbs?: BreadcrumbCrumb[];
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  width?: DashboardLayoutWidth;
}

/**
 * Page-level shell for dashboard / cockpit / wallet screens. Owns the
 * header → optional sticky bar → main content → optional footer rhythm
 * and renders a `PageHeader` at the top of the main column when a `title`
 * is provided.
 */
export const DashboardLayout = React.forwardRef<HTMLDivElement, DashboardLayoutProps>(
  function DashboardLayout(
    {
      actions,
      breadcrumbs,
      children,
      className,
      description,
      footer,
      header,
      stickyBar,
      stickyBarPosition = "sticky",
      title,
      width = "wide",
      ...props
    },
    ref,
  ) {
    return (
      <div ref={ref} className={cx("cw-v2-theme", "cw-v2-dashboard-layout", className)} {...props}>
        {header ? <div className="cw-v2-dashboard-layout__header">{header}</div> : null}
        {stickyBar ? (
          <div
            className={cx(
              "cw-v2-dashboard-layout__sticky-bar",
              `cw-v2-dashboard-layout__sticky-bar--${stickyBarPosition}`,
            )}
          >
            {stickyBar}
          </div>
        ) : null}
        <main className="cw-v2-dashboard-layout__main">
          <div
            className={cx(
              "cw-v2-dashboard-layout__container",
              `cw-v2-dashboard-layout__container--${width}`,
            )}
          >
            {title ? (
              <PageHeader
                actions={actions}
                breadcrumbs={breadcrumbs}
                className="cw-v2-dashboard-layout__title"
                description={description}
                title={title}
              />
            ) : null}
            {children}
          </div>
        </main>
        {footer ? <div className="cw-v2-dashboard-layout__footer">{footer}</div> : null}
      </div>
    );
  },
);
DashboardLayout.displayName = "DashboardLayout";
